import * as _Icon from '@radix-ui/react-accessible-icon';
import { icons, type Icons } from '@smart/react-icons';
import classNames from 'classnames';
import React from 'react';
import type { ExtendWithCommonProps, ResponsiveProperties } from '../../types';
import { joinClasses, responsiveClasses } from '../../utils';
import { Layout, type LayoutProps } from '../layout/layout';
import scss from './icon.module.scss';

type RemoveSizeSuffix<T extends string> = T extends
  | `${infer P}--medium`
  | `${infer P}--small`
  ? P
  : T;

type IconNamesWithoutSize = RemoveSizeSuffix<keyof typeof icons>;

export const iconCategories = Array.from(
  new Set(
    Object.keys(icons)
      .map((icon) => icon.replace('--small', ''))
      .map((icon) => icon.replace('--medium', '')),
  ),
) as IconNamesWithoutSize[];

export type IconCategories = (typeof iconCategories)[number];

export type IconProps = ExtendWithCommonProps<
  {
    /** Icon's name. */
    icon: IconCategories;
    /** Icon's size. */
    mode?: ResponsiveProperties<100 | 200 | 300 | 400>;
    /** Icon's color. */
    color?: LayoutProps['color'];
  },
  Omit<React.SVGProps<SVGSVGElement>, 'width' | 'height'>
>;

/**
 * Component used to display an icon.
 * Icons come in two sizes: `200` (16px) and `300` (24px). Note that these icons are uniquely designed for their respective sizes, rather than scaled versions of each other. As such, icons should not be resized.
 * Provide an `aria-label` to make the icon accessible.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-icon--docs
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/foundations-icons--docs
 **/
export const Icon = React.forwardRef<React.ElementRef<'svg'>, IconProps>(
  (
    {
      icon,
      mode = 200,
      color,
      className,
      'aria-label': ariaLabel,
      ...props
    }: IconProps,
    ref,
  ) => {
    if (!iconCategories.includes(icon as IconCategories)) {
      console.error(
        `Invalid icon name: ${icon}. Please choose from one of the following: ${iconCategories.join(', ')}`,
      );
      return <></>;
    }

    let iconName: Icons = `${icon}--small`;
    if (mode === 300 || mode === 400) {
      iconName = `${icon}--medium`;
    }
    const IconComponent = icons[iconName];

    const classes = classNames(
      scss.icon,
      joinClasses(responsiveClasses('mode', mode), scss),
      className,
    );

    const Component = (
      <Layout color={color} asChild>
        {<IconComponent ref={ref} className={classes} {...props} />}
      </Layout>
    );

    return ariaLabel ? (
      <_Icon.Root label={ariaLabel}>{Component}</_Icon.Root>
    ) : (
      Component
    );
  },
);

Icon.displayName = 'Icon';
